import React, { ReactElement, SVGProps } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { BellSvg } from '../../assets/svg-icon-components/BellSvg';
import { BookAltSvg } from '../../assets/svg-icon-components/BookAltSvg';
import { ChurchSvg } from '../../assets/svg-icon-components/ChurchSvg';
import { CalendarSvg } from '../../assets/svg-icon-components/CalendarSvg';
import { HomeSvg } from '../../assets/svg-icon-components/HomeSvg';
import { DonationSvg } from '../../assets/svg-icon-components/donationSvg';
import { UsersSvg } from '../../assets/svg-icon-components/UsersSvg';
import { UserRequestSvg } from '../../assets/svg-icon-components/UserRequestSvg';
import { routes } from '../../route-list';
import { history } from '../../util/history';
import { colors, Regular } from './Styles';
import { Can } from './rbac/Can';
import { useUserRoles } from '../../util/use-user-role';
import { RbacPermission } from './rbac/rbac-rules.c';

interface IItem {
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  title: string;
  path: string;
  perform: RbacPermission;
  subMenu?: { title: string; path: string }[];
}

const items: IItem[] = [
  {
    icon: HomeSvg,
    title: 'Home',
    path: routes.HOME,
    perform: 'read:dashboard',
  },
  {
    icon: UsersSvg,
    title: 'People',
    path: routes.PEOPLE,
    subMenu: [
      { title: 'Invite Someone', path: routes.PEOPLE__INVITE },
      { title: 'View People', path: routes.PEOPLE },
    ],
    perform: 'read:users',
  },
  {
    icon: BookAltSvg,
    title: 'Studies',
    path: routes.BIBLE_STUDIES,
    subMenu: [
      { title: 'Add New', path: routes.BIBLE_STUDIES__ADD },
      { title: 'View Bible Studies', path: routes.BIBLE_STUDIES },
    ],
    perform: 'read:programs',
  },
  {
    icon: CalendarSvg,
    title: 'Events',
    path: routes.EVENTS,
    perform: 'read:events',
  },
  {
    icon: BellSvg,
    title: 'Announcements',
    path: routes.COMMUNICATIONS,
    perform: 'read:notifications',
  },
  {
    icon: BookAltSvg,
    title: 'Mission Statement',
    path: routes.MISSION_STATEMENT,
    perform: 'read:mission_statements',
  },
  {
    icon: HomeSvg,
    title: 'Church',
    path: routes.CHURCH,
    perform: 'read:notifications',
  },
  {
    icon: DonationSvg,
    title: 'Donations',
    path: routes.DONATIONS,
    perform: 'read:donations',
  },
  {
    icon: UserRequestSvg,
    title: 'User Requests',
    path: routes.USER_REQUESTS,
    perform: 'read:user_requests',
  },
];

export const Sidebar: React.FC<{ allowSideMenu?: boolean }> = props => {
  const { allowSideMenu = false } = props;

  let { loading, roles } = useUserRoles();
  const location = useLocation();

  const currentPath = `/${location.pathname.split('/')[1]}`;
  const currentSubItemPath = `${currentPath}/${location.pathname.split('/')[2]}`;

  /*let { roles: myRoles, loading: isGettingRoles } = useUserRoles();*/
  const myRoles = roles;

  const myRole = myRoles?.[0];

  //dev
  const isSystemAdmin = myRole === 'System Admin';
  const isContentCreator = myRole === 'Content Creator';
  const isEventLeader = myRole === 'Event Leader';

  return (
    <SidebarNav>
      {items.map(item => {
        if (!isSystemAdmin && item.title == 'Church') {
          return '';
        }
        if ((isContentCreator || isEventLeader) && item.title === 'Announcements') {
          return '';
        }
        const isActive = currentPath === item.path;
        return (
          <Can
            key={item.path}
            role={roles?.[0]}
            perform={item.perform}
            yes={
              <div>
                <SidebarItem active={isActive}>
                  <SidebarButton
                    onClick={() => {
                      history.push(item.path);
                    }}>
                    {/* <Circle active={isActive} /> */}
                    <item.icon width={20} height={20} fill={isActive ? '#fff' : '#000'} />
                    <Regular color={isActive ? '#fff' : colors.normal}>{item.title}</Regular>
                  </SidebarButton>
                </SidebarItem>
                {allowSideMenu && isActive && !!item.subMenu && (
                  <SubMenuContainer>
                    <VerticalStick />
                    <SubMenu>
                      {item.subMenu.map(subItem => {
                        const isSubItemActive = currentSubItemPath === subItem.path;
                        return (
                          <SidebarItem key={subItem.path}>
                            <SidebarButton
                              onClick={() => {
                                history.push(subItem.path);
                              }}>
                              <Circle active={isSubItemActive} />
                              <Regular color={isSubItemActive ? colors.primary : colors.normal}>
                                {subItem.title}
                              </Regular>
                            </SidebarButton>
                          </SidebarItem>
                        );
                      })}
                    </SubMenu>
                  </SubMenuContainer>
                )}
              </div>
            }
          />
        );
      })}
    </SidebarNav>
  );
};

const SidebarNav = styled.nav`
  padding: 8px;
  background: #fff;
  /* 12em is the width of the columns */
  flex: 0 0 15em;
  /* put the nav on the left */
  order: -1;

  & > :not(:last-child) {
    margin-bottom: 14.5px;
  }
`;

const SidebarItem = styled.div<{ active?: boolean }>`
  padding: 12px 16px;
  ${({ active = false }) =>
    active &&
    css`
      background-color: ${colors.primary};
      border-radius: 6px;
    `}
`;

const SidebarButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  cursor: pointer;

  & > :not(:last-child) {
    margin-right: 20px;
  }
`;

const Circle = styled.div<{ active: boolean }>`
  width: 4px;
  height: 4px;
  border-radius: 2px;
  flex-shrink: 0;
  background: ${({ active }) => (active ? colors.primary : '#fff')};
`;

const SubMenuContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;

const VerticalStick = styled.div`
  margin-left: ${16 + 10}px;
  width: 2px;
  border-radius: 1px;
  background: rgba(0, 0, 0, 0.15);
`;

const SubMenu = styled.div`
  & > :not(:last-child) {
    margin-bottom: 8px;
  }
`;
